import { ChevronDown, ChevronUp, FileSearch2, Stamp, TriangleAlert, X } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

import { type DocumentWithExhibit, type ExhibitData, formatExhibitNumber } from "./exhibit-utils";

interface ReorderHandlers {
  onMoveUp: (boxFileId: string) => void;
  onMoveDown: (boxFileId: string) => void;
  onRemove: (boxFileId: string) => void;
}

interface ExhibitsListProps {
  exhibitData: ExhibitData;
  onPreview: (file: { id: string; name: string } | null) => void;
  /** When provided, each row gets up/down/remove buttons (manual ordering). */
  reorder?: ReorderHandlers;
  className?: string;
}

/** Port of exhibits/ExhibitsList.vue — grouped list of the pieces to stamp. */
export function ExhibitsList({ exhibitData, onPreview, reorder, className }: ExhibitsListProps) {
  const rows: DocumentWithExhibit[] = Array.from(exhibitData.grouped.values()).flat();
  const rowCount = rows.length;
  const flatIndexById = new Map(rows.map((doc, index) => [doc.id, index]));

  function renderRow(doc: DocumentWithExhibit, indexInGroup: number, flatIndex: number) {
    return (
      // biome-ignore lint/a11y/useSemanticElements: the row hosts nested reorder buttons, a <button> cannot
      <div
        key={doc.id}
        className={cn(
          "group flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 hover:ring-2 hover:ring-blue-300",
          doc.willBeStamped ? "bg-blue-50" : "ml-6 bg-gray-50",
          indexInGroup > 0 ? "border-l-2 border-gray-200" : "",
        )}
        onClick={() => onPreview(doc.file)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            onPreview(doc.file);
          }
        }}
        role="button"
        tabIndex={0}
      >
        {doc.willBeStamped ? (
          <Stamp className="h-4 w-4 shrink-0 text-blue-600" />
        ) : (
          <div className="h-4 w-4 shrink-0" />
        )}

        <span
          className={cn(
            "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
            doc.willBeStamped
              ? "bg-pink-50 text-pink-700 ring-pink-700/10"
              : "bg-gray-100 text-gray-500 ring-gray-500/10",
          )}
        >
          Pièce № {doc.exhibit ? formatExhibitNumber(doc.exhibit) : "?"}
        </span>

        <span
          className={cn("flex-1 truncate text-sm", doc.willBeStamped ? "text-gray-900" : "text-gray-500")}
        >
          {doc.file?.name || "Document sans nom"}
        </span>

        <FileSearch2 className="h-4 w-4 shrink-0 text-blue-500 opacity-0 transition-opacity duration-75 group-hover:opacity-100" />

        {reorder ? (
          <span className="flex shrink-0 items-center gap-0.5">
            <button
              type="button"
              aria-label="Monter la pièce"
              disabled={flatIndex === 0}
              className="rounded p-0.5 text-gray-400 hover:bg-blue-100 hover:text-gray-700 disabled:opacity-30"
              onClick={(event) => {
                event.stopPropagation();
                reorder.onMoveUp(doc.id);
              }}
            >
              <ChevronUp className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Descendre la pièce"
              disabled={flatIndex === rowCount - 1}
              className="rounded p-0.5 text-gray-400 hover:bg-blue-100 hover:text-gray-700 disabled:opacity-30"
              onClick={(event) => {
                event.stopPropagation();
                reorder.onMoveDown(doc.id);
              }}
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Retirer la pièce"
              className="rounded p-0.5 text-gray-400 hover:bg-red-100 hover:text-red-600"
              onClick={(event) => {
                event.stopPropagation();
                reorder.onRemove(doc.id);
              }}
            >
              <X className="h-4 w-4" />
            </button>
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className={cn("space-y-6", className)}>
      <p className="text-sm font-medium text-gray-600">{exhibitData.toStamp.length} pièces à tamponner</p>

      {exhibitData.missingNumbers.length > 0 ? (
        <Alert variant="error">
          <TriangleAlert />
          <AlertTitle>Pièces manquantes</AlertTitle>
          <AlertDescription>
            <div className="flex flex-wrap gap-2">
              {exhibitData.missingNumbers.map((num) => (
                <span
                  key={num}
                  className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-red-700/10 ring-inset"
                >
                  Pièce № {num}
                </span>
              ))}
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="space-y-1">
        {Array.from(exhibitData.grouped.entries()).map(([mainNumber, docs]) => (
          <div key={mainNumber} className="space-y-1">
            {docs.map((doc, indexInGroup) => renderRow(doc, indexInGroup, flatIndexById.get(doc.id) ?? 0))}
          </div>
        ))}
      </div>

      {exhibitData.withoutExhibits.length > 0 ? (
        <Alert>
          <TriangleAlert className="text-amber-500" />
          <AlertTitle>Documents sans numéro de pièce (non tamponnés)</AlertTitle>
          <AlertDescription>
            <ul className="space-y-1">
              {exhibitData.withoutExhibits.map((doc) => (
                <li key={doc.id}>
                  <button
                    type="button"
                    className="cursor-pointer truncate text-sm text-amber-600 hover:underline"
                    onClick={() => onPreview(doc.file)}
                  >
                    {doc.file?.name || "Document sans nom"}
                  </button>
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      {exhibitData.toStamp.length === 0 ? (
        <Alert className="text-center">
          <TriangleAlert className="mx-auto text-amber-500" />
          <AlertDescription className="col-span-2">
            <p>Aucun document avec numéro de pièce détecté</p>
            <p className="text-sm">Les documents doivent contenir "Pièce n°X" dans leur nom</p>
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
}
